// Third Party Libraries Imports
import toast from "react-hot-toast";
import { AxiosResponse } from "axios";
import { saveAs } from "file-saver";

// Local Imports
import instance from "../../store/axios";
import { LOADING } from "../../store/actions";

/**
 * Backend path. Every other service in the app prefixes the endpoint version,
 * so this does too — if the route is served without the `/v1` prefix, this is
 * the single line to change.
 */
export const FILL_CLIENT_IPS_PATH = `/${process.env.REACT_APP_ENDPOINT_VERSION}/files/daily-transaction-report/fill-client-ips`;

/** Multipart field name carrying the workbook. */
const FILE_FIELD = "file";

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** `{ data: "<base64 xlsx>", message: "Success" }` */
interface FillClientIpsResponse {
  data?: string;
  message?: string;
}

export interface FillResult {
  ok: boolean;
  /** Set when a workbook came back and was saved. */
  fileName?: string;
}

const outputName = (source: string): string =>
  `${source.replace(/\.(xlsx|xls|csv)$/i, "")}_with_client_ips.xlsx`;

/**
 * The response carries the workbook as base64, so it has to be turned back
 * into bytes before saving — writing the string itself would produce a file
 * that only looks like a workbook.
 */
const base64ToBlob = (base64: string): Blob => {
  const binary = atob(base64.replace(/^data:[^;]+;base64,/, ""));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: XLSX_MIME });
};

class TransactionReportIpService {
  private dispatch: any;

  constructor(dispatch?: any) {
    this.dispatch = dispatch;
  }

  private setLoading = (loading: boolean) => {
    if (this.dispatch) this.dispatch({ type: LOADING, payload: loading });
  };

  /**
   * Uploads the daily transaction report and saves the workbook that comes
   * back with `CLIENT_IP` filled in.
   */
  public async fillClientIps(file: File): Promise<FillResult> {
    this.setLoading(true);

    const formData = new FormData();
    formData.append(FILE_FIELD, file);

    try {
      const response: AxiosResponse<FillClientIpsResponse> =
        await instance.post(FILL_CLIENT_IPS_PATH, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

      const base64 = response.data?.data;

      if (!base64) {
        // 2xx with no workbook — report it rather than claim a download.
        toast.error(
          response.data?.message || "The server did not return a file."
        );
        return { ok: false };
      }

      const fileName = outputName(file.name);
      saveAs(base64ToBlob(base64), fileName);
      toast.success(response.data?.message || "Client IPs filled successfully");
      return { ok: true, fileName };
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "Failed to fill client IPs"
        );
      }
      localStorage.setItem("star_prev_url", "/customers/transaction-report-ip");
      return { ok: false };
    } finally {
      this.setLoading(false);
    }
  }
}

export default TransactionReportIpService;
